/**
 * AI Agent Service - Disaster Resume Generator
 * 
 * Processes raw API data from TomTom, OpenWeather, and USGS
 * into concise "Disaster Resumes" using Hugging Face inference.
 */

import { Alert, HazardType, LatLng } from '../types';
import { supabase } from './supabaseClient';

// Disaster Resume structure (output from AI)
export interface DisasterResume {
    type: 'Earthquake' | 'Flood' | 'Storm' | 'Wildfire' | 'Traffic Hazard' | 'Weather Alert' | 'News Report';
    severity: 'Low' | 'Moderate' | 'High' | 'Extreme';
    title: string;
    resume: string; // Max 150 chars for mobile UI
    location_label: string;
    action_step: string;
    is_critical: boolean;
    lat: number;
    lng: number;
}

// Raw data types from various APIs
export interface RawTomTomIncident {
    id: string;
    iconCategory: number;
    magnitudeOfDelay: number;
    description?: string;
    from?: string;
    to?: string;
    length?: number;
    delay?: number;
    roadNumbers?: string[];
    point: { latitude: number; longitude: number };
}

export interface RawUSGSEarthquake {
    id: string;
    properties: {
        mag: number;
        place: string;
        time: number;
        alert?: string;
        tsunami?: number;
        felt?: number;
        sig: number;
        title: string;
    };
    geometry: {
        coordinates: [number, number, number]; // [lng, lat, depth]
    };
}

export interface RawWeatherAlert {
    event: string;
    sender_name: string;
    start?: number;
    end?: number;
    description: string;
    tags?: string[];
    temp?: number;
    condition?: string;
    windBias?: number;
    isSevere?: boolean;
}

export interface RawRSSItem {
    title: string;
    link: string;
    pubDate: string;
    content: string;
    source: string;
}

// Hugging Face configuration
const HF_API_URL = 'https://api-inference.huggingface.co/models/mistralai/Mistral-7B-Instruct-v0.2';
const HF_API_KEY = import.meta.env.VITE_HF_TOKEN || '';

// Master prompt template
const MASTER_PROMPT = `<s>[INST] <<SYS>>
### ROLE
You are the core intelligence agent for Protectly AI (AïnRadar), a professional disaster management system. Your task is to process raw API data into a concise "Disaster Resume."

### GUIDELINES
1. **Tone**: Professional, urgent, and factual. No fluff.
2. **Conciseness**: The "resume" field MUST be under 150 characters for mobile UI compatibility.
3. **Severity Assessment**: Categorize the event as "Low", "Moderate", "High", or "Extreme" based on raw data metrics (e.g., magnitude, wind speed, or delay duration).
4. **Localization**: Ensure the location description is clear (e.g., "5km North of Martil, Morocco") rather than just raw coordinates.
5. **Output**: Return ONLY a valid JSON object. Do not include markdown code blocks, prose, or explanations.
<</SYS>>

### RAW DATA TO PROCESS
{{RAW_DATA}}

### OUTPUT JSON STRUCTURE
{
  "type": "Earthquake | Flood | Storm | Wildfire | Traffic Hazard | Weather Alert | News Report",
  "severity": "Low | Moderate | High | Extreme",
  "title": "Short title (e.g., 5.2 Magnitude Earthquake)",
  "resume": "A 1-2 sentence summary of the current situation and immediate impact.",
  "location_label": "Human readable name of the affected area",
  "action_step": "Recommended immediate action (e.g., Avoid the area, Seek high ground)",
  "is_critical": boolean,
  "lat": float,
  "lng": float
}

### RESPONSE: [/INST]`;

/**
 * Call Hugging Face inference API
 */
async function callHuggingFace(prompt: string): Promise<string> {
    if (!HF_API_KEY) {
        console.warn('⚠️ No Hugging Face API key configured, using fallback processing');
        throw new Error('HF_API_KEY not configured');
    }

    try {
        const response = await fetch(HF_API_URL, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${HF_API_KEY}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                inputs: prompt,
                parameters: {
                    temperature: 0.1, // Low temperature for consistent JSON output
                    max_new_tokens: 500,
                    return_full_text: false,
                    do_sample: true,
                }
            })
        });

        if (!response.ok) {
            throw new Error(`HF API error: ${response.status}`);
        }

        const result = await response.json();
        return result[0]?.generated_text || '';
    } catch (error) {
        console.error('Hugging Face API error:', error);
        throw error;
    }
}

/**
 * Parse AI response to DisasterResume
 */
function parseAIResponse(response: string): DisasterResume | null {
    try {
        // Try to extract JSON from response
        const jsonMatch = response.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
            const parsed = JSON.parse(jsonMatch[0]);
            // Validate required fields
            if (parsed.type && parsed.severity && parsed.resume) {
                // Enforce 150 char limit on resume
                if (parsed.resume.length > 150) {
                    parsed.resume = parsed.resume.substring(0, 147) + '...';
                }
                return parsed as DisasterResume;
            }
        }
        return null;
    } catch (error) {
        console.error('Failed to parse AI response:', error);
        return null;
    }
}

/**
 * Fallback processor for TomTom incidents (no AI)
 */
function processTomTomFallback(incident: RawTomTomIncident): DisasterResume {
    const categoryMap: Record<number, { type: string; icon: string }> = {
        1: { type: 'Traffic Hazard', icon: '🚗' },
        2: { type: 'Traffic Hazard', icon: '🚧' },
        3: { type: 'Traffic Hazard', icon: '⚠️' },
        6: { type: 'Traffic Hazard', icon: '🚧' },
        7: { type: 'Traffic Hazard', icon: '🔒' },
        8: { type: 'Traffic Hazard', icon: '❄️' },
        9: { type: 'Traffic Hazard', icon: '💥' },
        14: { type: 'Traffic Hazard', icon: '🚗' },
    };

    const category = categoryMap[incident.iconCategory] || { type: 'Traffic Hazard', icon: '⚠️' };

    // Determine severity based on delay magnitude
    let severity: 'Low' | 'Moderate' | 'High' | 'Extreme' = 'Low';
    if (incident.magnitudeOfDelay >= 4) severity = 'Extreme';
    else if (incident.magnitudeOfDelay >= 3) severity = 'High';
    else if (incident.magnitudeOfDelay >= 2) severity = 'Moderate';

    const delayMins = incident.delay ? Math.round(incident.delay / 60) : 0;
    const roadName = incident.roadNumbers?.join('/') || incident.from || 'Unknown Road';

    return {
        type: 'Traffic Hazard',
        severity,
        title: `${category.icon} ${incident.description || 'Traffic Incident'}`,
        resume: `${delayMins > 0 ? `${delayMins}min delay on ${roadName}.` : `Incident on ${roadName}.`} ${severity === 'Extreme' ? 'Major delays expected.' : ''}`.trim().substring(0, 150),
        location_label: incident.from || `${incident.point.latitude.toFixed(4)}, ${incident.point.longitude.toFixed(4)}`,
        action_step: severity === 'Extreme' ? 'Seek alternate route immediately' : 'Consider alternate routes',
        is_critical: severity === 'Extreme' || severity === 'High',
        lat: incident.point.latitude,
        lng: incident.point.longitude
    };
}

/**
 * Fallback processor for USGS earthquakes (no AI)
 */
function processUSGSFallback(quake: RawUSGSEarthquake): DisasterResume {
    const mag = quake.properties.mag;

    // Severity based on magnitude
    let severity: 'Low' | 'Moderate' | 'High' | 'Extreme' = 'Low';
    if (mag >= 7.0) severity = 'Extreme';
    else if (mag >= 5.5) severity = 'High';
    else if (mag >= 4.0) severity = 'Moderate';

    const [lng, lat, depth] = quake.geometry.coordinates;

    return {
        type: 'Earthquake',
        severity,
        title: `${mag.toFixed(1)}M Earthquake`,
        resume: `${mag.toFixed(1)} magnitude quake near ${quake.properties.place}. Depth: ${depth.toFixed(0)}km. ${quake.properties.tsunami ? '⚠️ Tsunami warning issued.' : ''}`.trim().substring(0, 150),
        location_label: quake.properties.place,
        action_step: severity === 'Extreme' ? 'DROP, COVER, HOLD ON. Move away from buildings.' : 'Stay alert for aftershocks.',
        is_critical: severity === 'Extreme' || severity === 'High',
        lat,
        lng
    };
}

/**
 * Fallback processor for Weather alerts (no AI)
 */
function processWeatherFallback(alert: RawWeatherAlert, lat: number, lng: number): DisasterResume {
    // Determine type and severity from event name
    let type: DisasterResume['type'] = 'Weather Alert';
    let severity: 'Low' | 'Moderate' | 'High' | 'Extreme' = 'Moderate';

    const eventLower = alert.event.toLowerCase();
    if (eventLower.includes('flood')) {
        type = 'Flood';
        severity = eventLower.includes('flash') ? 'Extreme' : 'High';
    } else if (eventLower.includes('storm') || eventLower.includes('thunder')) {
        type = 'Storm';
        severity = eventLower.includes('severe') ? 'High' : 'Moderate';
    } else if (eventLower.includes('fire') || eventLower.includes('wildfire')) {
        type = 'Wildfire';
        severity = 'Extreme';
    } else if (eventLower.includes('warning')) {
        severity = 'High';
    } else if (eventLower.includes('watch')) {
        severity = 'Moderate';
    }

    return {
        type,
        severity,
        title: alert.event,
        resume: alert.description.substring(0, 147) + (alert.description.length > 147 ? '...' : ''),
        location_label: alert.sender_name || 'Your Area',
        action_step: severity === 'Extreme' ? 'Take immediate protective action' : 'Monitor conditions closely',
        is_critical: severity === 'Extreme' || severity === 'High',
        lat,
        lng
    };
}

/**
 * Process raw data with AI (with fallback)
 */
export async function generateDisasterResumeWithAI(
    rawData: RawTomTomIncident | RawUSGSEarthquake | RawWeatherAlert | RawRSSItem,
    dataType: 'tomtom' | 'usgs' | 'weather' | 'rss',
    fallbackCoords?: LatLng
): Promise<DisasterResume> {
    // 1. Try Supabase Edge Function first (Server-side AI)
    try {
        const { data, error } = await supabase.functions.invoke('process-disaster-resume', {
            body: { rawData, dataType }
        });

        if (!error && data?.resume) {

            // Store valid resume in DB
            await saveResumeToSupabase(data.resume);
            return data.resume;
        }
    } catch (edgeError) {
        console.warn('⚠️ Edge Function failed, falling back to local/client AI', edgeError);
    }

    // 2. Fallback to Client-side AI (Hugging Face direct)
    try {
        const prompt = MASTER_PROMPT.replace('{{RAW_DATA}}', JSON.stringify(rawData, null, 2));
        const aiResponse = await callHuggingFace(prompt);
        const parsed = parseAIResponse(aiResponse);

        if (parsed) {
            // Fallback to client-side rule-based logic

            await saveResumeToSupabase(parsed);
            return parsed;
        }
    } catch (error) {
        console.warn('⚠️ Client-side AI failed, using rule-based fallback');
    }

    // 3. Final Fallback: Rule-based processing

    let fallbackResume: DisasterResume;

    switch (dataType) {
        case 'tomtom':
            fallbackResume = processTomTomFallback(rawData as RawTomTomIncident);
            break;
        case 'usgs':
            fallbackResume = processUSGSFallback(rawData as RawUSGSEarthquake);
            break;
        case 'weather':
            fallbackResume = processWeatherFallback(
                rawData as RawWeatherAlert,
                fallbackCoords?.lat || 0,
                fallbackCoords?.lng || 0
            );
            break;
        case 'rss':
            // Basic fallback for RSS if AI fails
            const rss = rawData as RawRSSItem;
            fallbackResume = {
                type: 'News Report',
                severity: 'Moderate',
                title: rss.title || 'News Update',
                resume: (rss.content || rss.title || '').substring(0, 150),
                location_label: 'Regional News',
                action_step: 'Monitor local media',
                is_critical: false,
                lat: fallbackCoords?.lat || 0,
                lng: fallbackCoords?.lng || 0
            };
            break;
        default:
            throw new Error('Unknown data type');
    }

    // Save fallback resume too (marked as such if needed, but schema is same)
    await saveResumeToSupabase(fallbackResume);
    return fallbackResume;
}

/**
 * Persist generated resume to Supabase 'hazards' table
 */
async function saveResumeToSupabase(resume: DisasterResume) {
    try {
        const alert = resumeToAlert(resume);

        // Check for duplicates based on location/time/type to avoid spam
        // For simplicity, we just insert. RLS or unique constraints should handle dupes in prod.
        const { error } = await supabase
            .from('hazards')
            .upsert({
                type: alert.type,
                severity: alert.severity,
                location_lat: alert.coordinates.lat,
                location_lng: alert.coordinates.lng,
                location: alert.location, // Fix: Save human-readable location
                description: alert.description,
                instructions: alert.advice,
                is_active: true,
                source: 'AI_AGENT',
                metadata: resume
            }, { onConflict: 'id' as never }); // 'never' if we don't have a stable ID, or use a generated one

        if (error) console.error('Failed to save resume to Supabase:', error);
        else { /* Resume saved to Supabase DB */ }

    } catch (e) {
        console.error('Error saving to Supabase:', e);
    }
}

/**
 * Batch process multiple incidents
 */
export async function batchGenerateResumes(
    incidents: Array<{ data: any; type: 'tomtom' | 'usgs' | 'weather' }>,
    options?: { maxConcurrent?: number }
): Promise<DisasterResume[]> {
    const maxConcurrent = options?.maxConcurrent || 3;
    const results: DisasterResume[] = [];

    // Process in batches to avoid rate limiting
    for (let i = 0; i < incidents.length; i += maxConcurrent) {
        const batch = incidents.slice(i, i + maxConcurrent);
        const batchResults = await Promise.all(
            batch.map(({ data, type }) => generateDisasterResumeWithAI(data, type))
        );
        results.push(...batchResults);
    }

    return results;
}

/**
 * Convert DisasterResume to Alert (for map display)
 */
export function resumeToAlert(resume: DisasterResume): Alert {
    // Map resume type to HazardType
    const typeMap: Record<string, HazardType> = {
        'Earthquake': HazardType.EARTHQUAKE,
        'Flood': HazardType.FLOOD,
        'Storm': HazardType.WEATHER,
        'Wildfire': HazardType.FIRE,
        'Traffic Hazard': HazardType.TRAFFIC,
        'Weather Alert': HazardType.WEATHER,
        'News Report': HazardType.CIVIL, // Fallback mapping
    };

    const severityMap: Record<string, 'low' | 'medium' | 'high' | 'critical'> = {
        'Low': 'low',
        'Moderate': 'medium',
        'High': 'high',
        'Extreme': 'critical',
    };

    return {
        id: `ai-resume-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: typeMap[resume.type] || HazardType.WEATHER,
        severity: severityMap[resume.severity] || 'medium',
        location: resume.location_label,
        coordinates: { lat: resume.lat, lng: resume.lng },
        timestamp: new Date().toISOString(),
        distance: '0km',
        isOfficial: true,
        source: 'Protectly AI Intelligence',
        description: resume.resume,
        advice: [resume.action_step],
        truthScore: 95,
        isRedAlert: resume.is_critical,
    };
}

export default {
    generateDisasterResumeWithAI,
    batchGenerateResumes,
    resumeToAlert,
};
